import { useEffect, useState } from "react";
import { useLocalizedStrings } from "./Localization";
import { AidKitLogo } from "./Util";

/**
 * Shape of /maintenance.json, published to the root of the frontend bucket by `./cli setMaintenanceMode`.
 * `warning` has the same shape as the global banner: { "en": "...", "es": "...", "__style": "..." }.
 * Keep in sync with MaintenanceStatus in packages/aidkit/lib/common/maintenance.ts.
 */
type MaintenanceStatus = {
  warning?: Record<string, string>,
  blocking?: boolean,
  bypass?: string,
};

const BYPASS_KEY = 'maintenance_bypass';

/**
 * Reads the maintenance status once on page load. Testers skip the block by visiting any page with
 * ?maintenance_bypass=<secret>, which is remembered in localStorage so it survives navigation and login.
 */
export function useMaintenanceStatus(localStorage: Storage) {
  const [status, setStatus] = useState<MaintenanceStatus>({});

  useEffect(() => {
    const bypassParam = new URLSearchParams(window.location.search).get(BYPASS_KEY);
    if (bypassParam) {
      localStorage.setItem(BYPASS_KEY, bypassParam);
    }

    (async () => {
      try {
        const resp = await fetch('/maintenance.json', { cache: 'no-store' });
        if (!resp.ok) return;
        const json = await resp.json();
        if (json && typeof json === 'object') setStatus(json);
      } catch (e) {
        // Fail open: a missing file is served the SPA's index.html, which fails to parse as JSON.
      }
    })();
  }, []);

  const bypassed = !!status.bypass && localStorage.getItem(BYPASS_KEY) === status.bypass;
  return { warning: status.warning, blocked: !!status.blocking && !bypassed };
}

export function MaintenancePage() {
  const L = useLocalizedStrings();
  return (
    <div className="flex flex-col items-center justify-center px-4 text-center bg-indigo-100" style={{ minHeight: '100vh' }}>
      <AidKitLogo width={100} height={50} />
      <h1 className="mt-8 text-4xl font-extrabold text-gray-900 tracking-tight sm:text-5xl">{L.maintenance.title}</h1>
      <p className="mt-4 max-w-xl text-base text-gray-500">{L.maintenance.message}</p>
    </div>
  );
}
